import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as api from '@/api/modules/suggestions'
import { SUGGESTION_COPY as COPY } from './copy'
import { INBOX_STATUSES } from './constants'

export { INBOX_STATUSES, OPEN_STATUSES } from './constants'

/**
 * Metric suggestions in the client: one run per connection and surface (the
 * onboarding step never shows a panel or assistant run, R6/R14), the inbox
 * list, and decisions in
 * flight. A decision replaces the card wherever it is shown. Accepting shows
 * the card as `accepted` at once, then follows the setup until it is
 * `applied` or `apply_failed`.
 *
 * A model run is polled until the backend settles it: the backend answers a
 * lost job with the rules about 150 s after it was queued, so `MAX_POLLS` is
 * only a backstop past that bound. After `STILL_WORKING_AFTER_MS` the run is
 * flagged `isStillWorking` so the page can say so.
 */

export const POLL_INTERVAL_MS = 1500
/** Backstop only: about 300 s, twice the backend's own 150 s bound. */
export const MAX_POLLS = 200
export const STILL_WORKING_AFTER_MS = 90_000

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/** Where a run lives in `state.runs`: one per connection and surface. */
export const runKey = (connectionId, surface) => `${connectionId}:${surface}`
const runKeyOf = (arg) => runKey(arg.connectionId, arg.surface)

/** An error whose `userMessage` is safe to show as is. */
function runError(message, userMessage) {
  return Object.assign(new Error(message), { userMessage })
}

/** A message for the UI: the server's, ours, or `fallback` (never a raw JS error). */
const messageFor = (error, fallback) => error?.apiError?.message || error?.userMessage || fallback

/**
 * Poll a model run until the backend settles it.
 *
 * @param {string} connectionId
 * @param {string} jobId
 * @param {{sleep?: Function, interval?: number, maxPolls?: number, onStillWorking?: Function}} [options]
 * @returns {Promise<Object>} the run's result
 */
export async function pollRun(connectionId, jobId, { sleep = wait, interval = POLL_INTERVAL_MS, maxPolls = MAX_POLLS, onStillWorking } = {}) {
  const stillWorkingAt = Math.ceil(STILL_WORKING_AFTER_MS / interval)
  for (let attempt = 0; attempt < maxPolls; attempt += 1) {
    if (attempt === stillWorkingAt) onStillWorking?.()
    const job = await api.fetchSuggestionRun(connectionId, jobId)
    if (job?.status === 'done') return job.result
    if (job?.status === 'failed') throw runError(job.message || COPY.runFailed, job.message || COPY.runFailed)
    await sleep(interval)
  }
  throw runError('timeout', COPY.runTimedOut)
}

async function followSetup(suggestion, sleep) {
  let current = suggestion
  for (let attempt = 0; current?.status === 'accepted' && attempt < MAX_POLLS; attempt += 1) {
    await sleep(POLL_INTERVAL_MS)
    try {
      current = await api.fetchSuggestion(current.id)
    } catch (error) {
      throw runError(error?.message || 'setup poll failed', COPY.setupCheckFailed)
    }
  }
  return current
}

export const runSuggestions = createAsyncThunk(
  'suggestions/run',
  async ({ connectionId, surface, topic, k, sleep }, { dispatch, rejectWithValue }) => {
    try {
      const start = await api.startSuggestionRun(connectionId, { surface, topic, k })
      if (start.result) return start.result
      const onStillWorking = () => dispatch(runStillWorking({ connectionId, surface }))
      return await pollRun(connectionId, start.jobId, { sleep, onStillWorking })
    } catch (error) {
      return rejectWithValue(messageFor(error, COPY.runFailed))
    }
  },
  // One run per connection and surface at a time (StrictMode runs effects twice).
  { condition: ({ connectionId, surface }, { getState }) => selectRun(getState(), connectionId, surface).status !== 'running' },
)

export const loadInbox = createAsyncThunk('suggestions/loadInbox', async ({ connectionId, status } = {}, { rejectWithValue }) => {
  try {
    return await api.fetchSuggestions({ connectionId, status: status || INBOX_STATUSES.join(',') })
  } catch (error) {
    return rejectWithValue(messageFor(error, COPY.loadFailed))
  }
})

async function accept(id, sleep, dispatch) {
  const accepted = (await api.acceptSuggestion(id)) ?? (await api.fetchSuggestion(id))
  if (accepted?.id) dispatch(suggestionUpdated(accepted))
  return followSetup(accepted, sleep)
}

/** A rejected payload is the server's message, or null when there is none to show. */
export const decideSuggestion = createAsyncThunk(
  'suggestions/decide',
  async ({ id, action, reason, sleep = wait }, { dispatch, rejectWithValue }) => {
    try {
      if (action === 'accept') return await accept(id, sleep, dispatch)
      if (action === 'snooze') return await api.snoozeSuggestion(id)
      if (action === 'dismiss') return await api.dismissSuggestion(id, { reason })
      // Never a silent dismissal: only an explicit 'dismiss' hides a suggestion for 180 days.
      return rejectWithValue(COPY.decideFailed)
    } catch (error) {
      return rejectWithValue(messageFor(error, null))
    }
  },
  // One decision per card at a time: a second click while the first is in flight is dropped.
  { condition: ({ id }, { getState }) => !selectIsDeciding(getState(), id) },
)

const replaceIn = (list, updated) => (list || []).map((item) => (item.id === updated.id ? updated : item))

/** Whether `action` answers the latest request for what it was stored under (a late, older answer does not). */
const isLatest = (stored, action) => stored?.requestId === action.meta.requestId

/** Replace `updated` wherever it is shown: the inbox and every run. */
function replaceEverywhere(state, updated) {
  if (!updated?.id) return
  state.inbox.items = replaceIn(state.inbox.items, updated)
  Object.values(state.runs).forEach((run) => {
    if (!run.result) return
    run.result.suggestions = replaceIn(run.result.suggestions, updated)
    run.result.more_ideas = replaceIn(run.result.more_ideas, updated)
  })
}

const stopDeciding = (state, id) => {
  state.decidingIds = state.decidingIds.filter((decidingId) => decidingId !== id)
}

const initialState = { inbox: { items: [], count: 0, status: 'idle', error: null }, runs: {}, decidingIds: [] }

const suggestionsSlice = createSlice({
  name: 'suggestions',
  initialState,
  reducers: {
    runStillWorking: (state, action) => {
      const run = state.runs[runKeyOf(action.payload)]
      if (run?.status === 'running') run.isStillWorking = true
    },
    suggestionUpdated: (state, action) => replaceEverywhere(state, action.payload),
  },
  extraReducers: (builder) => {
    builder
      .addCase(runSuggestions.pending, (state, action) => {
        const { requestId } = action.meta
        state.runs[runKeyOf(action.meta.arg)] = { status: 'running', result: null, error: null, isStillWorking: false, requestId }
      })
      .addCase(runSuggestions.fulfilled, (state, action) => {
        const key = runKeyOf(action.meta.arg)
        if (!isLatest(state.runs[key], action)) return
        state.runs[key] = { status: 'done', result: action.payload, error: null, requestId: action.meta.requestId }
      })
      .addCase(runSuggestions.rejected, (state, action) => {
        const key = runKeyOf(action.meta.arg)
        if (!isLatest(state.runs[key], action)) return
        state.runs[key] = { status: 'failed', result: null, error: action.payload ?? COPY.runFailed, requestId: action.meta.requestId }
      })
      .addCase(loadInbox.pending, (state, action) => {
        state.inbox.status = 'loading'
        state.inbox.error = null
        state.inbox.requestId = action.meta.requestId
      })
      .addCase(loadInbox.fulfilled, (state, action) => {
        if (!isLatest(state.inbox, action)) return
        const { results, count } = action.payload
        state.inbox = { items: results, count, status: 'done', error: null, requestId: action.meta.requestId }
      })
      .addCase(loadInbox.rejected, (state, action) => {
        if (!isLatest(state.inbox, action)) return
        state.inbox.status = 'failed'
        state.inbox.error = action.payload ?? COPY.loadFailed
      })
      .addCase(decideSuggestion.pending, (state, action) => {
        state.decidingIds.push(action.meta.arg.id)
      })
      .addCase(decideSuggestion.fulfilled, (state, action) => {
        stopDeciding(state, action.meta.arg.id)
        replaceEverywhere(state, action.payload)
      })
      .addCase(decideSuggestion.rejected, (state, action) => {
        if (!action.meta.condition) stopDeciding(state, action.meta.arg.id)
      })
  },
})

const { runStillWorking, suggestionUpdated } = suggestionsSlice.actions

const IDLE_RUN = Object.freeze({ status: 'idle', result: null, error: null })

export const selectInbox = (state) => state.suggestions.inbox
/**
 * @param {Object} state
 * @param {string} connectionId
 * @param {string} surface - `onboarding`, `assistant` or `ongoing`
 * @returns {{status: 'idle'|'running'|'done'|'failed', result: Object|null, error: string|null, isStillWorking?: boolean}}
 *   `isStillWorking` is set only while running.
 */
export const selectRun = (state, connectionId, surface) => state.suggestions.runs[runKey(connectionId, surface)] ?? IDLE_RUN
export const selectDecidingIds = (state) => state.suggestions.decidingIds
export const selectIsDeciding = (state, id) => state.suggestions.decidingIds.includes(id)

export default suggestionsSlice.reducer
